import { useRouter, useSearch } from "@tanstack/react-router"
import React, { useEffect, useMemo, useState } from "react"

import { trpc } from "../lib/trpc"

interface FilterState {
  genres: string[]
  bpmRange: { min: number; max: number } | null
  keys: string[]
  search: string
  hiddenIncluded: boolean
}

const GENRE_OPTIONS = ["pop", "bollywood", "edm", "hiphop"]
const CAMELOT_KEYS = [
  "1A",
  "2A",
  "3A",
  "4A",
  "5A",
  "6A",
  "7A",
  "8A",
  "9A",
  "10A",
  "11A",
  "12A",
  "1B",
  "2B",
  "3B",
  "4B",
  "5B",
  "6B",
  "7B",
  "8B",
  "9B",
  "10B",
  "11B",
  "12B",
]

export default function MusicCatalogScreen() {
  const router = useRouter()
  const [isConnecting, setIsConnecting] = useState(false)

  const [filters, setFilters] = useState<FilterState>({
    genres: [],
    bpmRange: null,
    keys: [],
    search: "",
    hiddenIncluded: false,
  })
  const [page, setPage] = useState(0)
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null)

  const limit = 20
  const offset = page * limit

  // Fetch tracks with current filters
  const { data: tracks, isLoading } = trpc.music.getTracks.useQuery({
    genres: filters.genres.length > 0 ? (filters.genres as any) : undefined,
    bpmRange: filters.bpmRange || undefined,
    keys: filters.keys.length > 0 ? filters.keys : undefined,
    search: filters.search || undefined,
    hiddenIncluded: filters.hiddenIncluded,
    limit,
    offset,
  })

  // Fetch track count for pagination
  const { data: totalCount } = trpc.music.getTrackCount.useQuery({
    genres: filters.genres.length > 0 ? (filters.genres as any) : undefined,
    bpmRange: filters.bpmRange || undefined,
    keys: filters.keys.length > 0 ? filters.keys : undefined,
    search: filters.search || undefined,
    hiddenIncluded: filters.hiddenIncluded,
  })

  // Fetch mix suggestions for selected track
  const { data: mixSuggestions } = trpc.music.getMixSuggestions.useQuery(
    {
      trackId: selectedTrack,
      maxSuggestions: 5,
    },
    { enabled: !!selectedTrack },
  )

  // Mutations
  const toggleFavorite = trpc.music.toggleFavorite.useMutation()
  const toggleHidden = trpc.music.toggleHidden.useMutation()

  // tRPC queries and mutations
  const { data: connectionStatus, refetch: refetchStatus } = trpc.spotify.getConnectionStatus.useQuery()
  const { data: playlists } = trpc.spotify.getUserPlaylists.useQuery(
    { limit: 20 },
    { enabled: connectionStatus?.isConnected },
  )

  const getAuthUrlMutation = trpc.spotify.getAuthUrl.useMutation()
  const handleCallbackMutation = trpc.spotify.handleCallback.useMutation()
  // const testConnectionMutation = trpc.spotify.testConnection.useMutation(); // TODO: testConnection should be a query, not mutation

  // Handle OAuth callback
  useEffect(() => {
    const handleSpotifyCallback = async () => {
      const params = new URLSearchParams(window.location.search)
      const code = params.get("code")
      const state = params.get("state")
      const error = params.get("error")
      const isCallback = params.get("spotify_callback")

      if (isCallback && code) {
        setIsConnecting(true)
        try {
          await handleCallbackMutation.mutateAsync({
            code,
            state: state || undefined,
            redirectUri: "https://agent.aixlabs.co/api/spotify/callback",
          })

          // Clear URL parameters and refresh status
          router.navigate({
            to: "/music",
            // search: {}
          })
          await refetchStatus()

          console.log("✅ Spotify connected successfully!")
        } catch (error) {
          console.error("❌ Failed to connect Spotify:", error)
        } finally {
          setIsConnecting(false)
        }
      } else if (error) {
        console.error("❌ Spotify OAuth error:", error)
      }
    }

    void handleSpotifyCallback()
  }, [handleCallbackMutation, refetchStatus, router])

  const handleConnectSpotify = async () => {
    try {
      const { authUrl } = await getAuthUrlMutation.mutateAsync({
        redirectUri: "https://agent.aixlabs.co/api/spotify/callback",
      })

      // Redirect to Spotify OAuth
      window.location.href = authUrl
    } catch (error) {
      console.error("Failed to get Spotify auth URL:", error)
    }
  }

  const handleTestConnection = async () => {
    try {
      // TODO: Replace with proper testConnection query
      // const result = await testConnectionMutation.mutateAsync();
      console.log("Test connection - TODO: implement")
      await refetchStatus()
    } catch (error) {
      console.error("❌ Connection test failed:", error)
    }
  }

  const totalPages = Math.ceil((totalCount || 0) / limit)

  const handleGenreToggle = (genre: string) => {
    setFilters((prev) => ({
      ...prev,
      genres: prev.genres.includes(genre) ? prev.genres.filter((g) => g !== genre) : [...prev.genres, genre],
    }))
    setPage(0)
  }

  const handleKeyToggle = (key: string) => {
    setFilters((prev) => ({
      ...prev,
      keys: prev.keys.includes(key) ? prev.keys.filter((k) => k !== key) : [...prev.keys, key],
    }))
    setPage(0)
  }

  const handleBpmRangeChange = (min: number, max: number) => {
    setFilters((prev) => ({
      ...prev,
      bpmRange: { min, max },
    }))
    setPage(0)
  }

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search }))
    setPage(0)
  }

  const handleToggleFavorite = async (trackId: string) => {
    try {
      await toggleFavorite.mutateAsync({ trackId })
      // Refetch tracks to update UI
      window.location.reload() // Simple approach for demo
    } catch (error) {
      console.error("Failed to toggle favorite:", error)
    }
  }

  const handleToggleHidden = async (trackId: string) => {
    try {
      await toggleHidden.mutateAsync({ trackId })
      window.location.reload() // Simple approach for demo
    } catch (error) {
      console.error("Failed to toggle hidden:", error)
    }
  }

  if (isConnecting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-lg">Connecting to Spotify...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Music Catalog</h1>

      {/* Connection Status */}
      <div className="bg-background rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Spotify Connection</h2>

        {connectionStatus?.isConnected ? (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-green-700 font-medium">Connected to Spotify</span>
            </div>

            {connectionStatus.userInfo && (
              <div className="text-sm ">
                <p>
                  <strong>Display Name:</strong> {connectionStatus.userInfo.displayName}
                </p>
                <p>
                  <strong>Email:</strong> {connectionStatus.userInfo.email}
                </p>
                <p>
                  <strong>Last Sync:</strong>{" "}
                  {connectionStatus.lastSync ? new Date(connectionStatus.lastSync).toLocaleString() : "Never"}
                </p>
              </div>
            )}

            <button
              onClick={handleTestConnection}
              disabled={false}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              Test Connection
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-destructive font-medium">Not Connected</span>
            </div>

            <button
              onClick={handleConnectSpotify}
              disabled={getAuthUrlMutation.isLoading}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              {getAuthUrlMutation.isLoading ? "Loading..." : "Connect Spotify"}
            </button>
          </div>
        )}
      </div>

      {/* Playlists */}
      {connectionStatus?.isConnected && (
        <div className="bg-background rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Your Playlists</h2>

          {playlists?.items?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {playlists.items.map((playlist: any) => (
                <div key={playlist.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  {playlist.images?.[0]?.url && (
                    <img
                      src={playlist.images[0].url}
                      alt={playlist.name}
                      className="w-full h-48 object-cover rounded-md mb-3"
                    />
                  )}
                  <h3 className="font-semibold text-lg">{playlist.name}</h3>
                  <p className=" text-sm mt-1">{playlist.tracks.total} tracks</p>
                  {playlist.description && (
                    <p className="text-gray-500 text-sm mt-2 line-clamp-2">{playlist.description}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No playlists found.</p>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-background p-6 rounded-lg shadow-sm mb-6">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>

        {/* Search */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-primary mb-2">Search</label>
          <input
            type="text"
            className="w-full p-2 border border-gray-300 rounded-md"
            placeholder="Search tracks, artists, albums..."
            value={filters.search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        {/* Genres */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-primary mb-2">Genres</label>
          <div className="flex flex-wrap gap-2">
            {GENRE_OPTIONS.map((genre) => (
              <button
                key={genre}
                onClick={() => handleGenreToggle(genre)}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  filters.genres.includes(genre)
                    ? "bg-blue-500 text-white"
                    : "bg-foregroundx text-primary hover:bg-gray-300"
                }`}
              >
                {genre.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* BPM Range */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-primary mb-2">BPM Range</label>
          <div className="flex gap-4">
            <input
              type="number"
              placeholder="Min BPM"
              className="p-2 border border-gray-300 rounded-md w-24"
              value={filters.bpmRange?.min || ""}
              onChange={(e) => {
                const min = parseInt(e.target.value) || 50
                const max = filters.bpmRange?.max || 200
                handleBpmRangeChange(min, max)
              }}
            />
            <span className="self-center">-</span>
            <input
              type="number"
              placeholder="Max BPM"
              className="p-2 border border-gray-300 rounded-md w-24"
              value={filters.bpmRange?.max || ""}
              onChange={(e) => {
                const max = parseInt(e.target.value) || 200
                const min = filters.bpmRange?.min || 50
                handleBpmRangeChange(min, max)
              }}
            />
          </div>
        </div>

        {/* Camelot Keys */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-primary mb-2">Camelot Keys</label>
          <div className="grid grid-cols-12 gap-1">
            {CAMELOT_KEYS.map((key) => (
              <button
                key={key}
                onClick={() => handleKeyToggle(key)}
                className={`p-1 text-xs rounded ${
                  filters.keys.includes(key)
                    ? "bg-green-500 text-white"
                    : "bg-foregroundx text-primary hover:bg-gray-300"
                }`}
              >
                {key}
              </button>
            ))}
          </div>
        </div>

        {/* Show Hidden */}
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.hiddenIncluded}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  hiddenIncluded: e.target.checked,
                }))
              }
              className="mr-2"
            />
            <span className="text-sm text-primary">Include hidden tracks</span>
          </label>
        </div>
      </div>

      {/* Tracks List */}
      <div className="bg-background rounded-lg shadow-sm">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Tracks ({totalCount || 0} total)</h2>
        </div>

        {isLoading ? (
          <div className="p-6">Loading tracks...</div>
        ) : !tracks || tracks.length === 0 ? (
          <div className="p-6 text-gray-500">No tracks found</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {tracks.map((track) => (
              <div key={track.id} className="p-4 ">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium ">{track.title}</h3>
                    <p className="text-sm ">{track.artist}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{track.genre.toUpperCase()}</span>
                      <span>{track.bpm} BPM</span>
                      {track.camelotKey && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">{track.camelotKey}</span>
                      )}
                      {track.energy && <span>{track.energy}% Energy</span>}
                      <span>★ {track.popularity}/100</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleFavorite(track.id)}
                      className={`p-2 rounded ${
                        track.isFavorite
                          ? "text-destructive hover:text-destructive"
                          : "text-gray-400 hover:text-destructive"
                      }`}
                    >
                      ❤️
                    </button>

                    <button
                      onClick={() => handleToggleHidden(track.id)}
                      className={`p-2 rounded ${track.isHidden ? "" : "text-gray-400 hover:"}`}
                    >
                      👁️
                    </button>

                    <button
                      onClick={() => setSelectedTrack(track.id)}
                      className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                    >
                      Mix
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex justify-between items-center">
            <div className="text-sm ">
              Page {page + 1} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 bg-foregroundx text-primary rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 bg-foregroundx text-primary rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mix Suggestions Modal */}
      {selectedTrack && mixSuggestions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-background rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-lg font-semibold">Mix Suggestions</h2>
              <button onClick={() => setSelectedTrack(null)} className="absolute top-4 right-4 text-gray-400 hover:">
                ✕
              </button>
            </div>

            <div className="p-6">
              {mixSuggestions.length === 0 ? (
                <p className="text-gray-500">No compatible tracks found</p>
              ) : (
                <div className="space-y-4">
                  {mixSuggestions.map((suggestion, index) => (
                    <div key={suggestion.track.id} className="border border-border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-medium">{suggestion.track.title}</h3>
                          <p className="text-sm ">{suggestion.track.artist}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold text-green-600">{suggestion.score.overallScore}%</div>
                          <div className="text-xs text-gray-500">{suggestion.transitionType}</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-2 text-xs mb-3">
                        <div>
                          <span className="text-gray-500">Key:</span> {suggestion.score.keyScore}%
                        </div>
                        <div>
                          <span className="text-gray-500">BPM:</span> {suggestion.score.bpmScore}%
                        </div>
                        <div>
                          <span className="text-gray-500">Energy:</span> {suggestion.score.energyScore}%
                        </div>
                        <div>
                          <span className="text-gray-500">Genre:</span> {suggestion.score.genreScore}%
                        </div>
                      </div>

                      <div className="text-sm text-primary bg-foregroundx p-3 rounded">
                        <strong>Transition Notes:</strong> {suggestion.transitionNotes}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
